import React, { useContext, useEffect, useRef, useState } from 'react';
import { Routes, Route, Navigate, useLocation, useParams, useNavigate } from 'react-router-dom';
import { generateClient } from '@aws-amplify/api';
import RootContext from '@/RootContext';
import AgentSettingsList from '@/AgentSettingsList';
import AgentSettingEntry from '@/AgentSettingEntry';
import AIPlatformEntry from '@/AIPlatformEntry';
import DealEntry from '@/DealEntry';
import DealsList from '@/DealsList';
import ProtocolEntry from '@/ProtocolEntry';
import ProtocolsList from '@/ProtocolsList';
import SegmentsList from '@/SegmentsList';
import SegmentEntryV1 from '@/SegmentEntryV1';
import SitesList from '@/SitesList';
import SiteEntry from '@/SiteEntry';
import StudiesList from '@/StudiesList';
import StudyEntry from '@/StudyEntry';
import SubjectsList from '@/SubjectsList'; 
import SubjectEntry from '@/SubjectEntry';
import RecruitmentSourcesList from '@/RecruitmentSourcesList';
import RecruitmentSourceEntry from '@/RecruitmentSourceEntry';
import WorkflowsList from '@/WorkflowsList';
import WorkflowBuilder from '@/WorkflowEntry/WorkflowBuilder';
import QuestionnaireLayout from '@/QuestionnaireBuilder/QuestionnaireLayout';
import QuestionnaireSetup from '@/QuestionnaireBuilder/QuestionnaireSetup';
import QuestionnaireDetails from '@/QuestionnaireBuilder/QuestionnaireDetails';
import QuestionnaireQuestions from '@/QuestionnaireBuilder/QuestionnaireQuestions';
import QuestionnaireReview from '@/QuestionnaireBuilder/QuestionnaireReview';
import QuestionnaireList from '@/QuestionnaireBuilder/QuestionnaireList';
import Privacy from '@/Privacy';
import Header from '@/Header';

const client = generateClient();

const SpinnerLine = () => (
    <span className="spinner-border spinner-border-sm" style={{ width: '1.5rem', height: '1.5rem', borderWidth: '2px', opacity:0.5, zIndex:1 }} />
);

const Name = ({ id, type }) => {
  const [name, setName] = useState(<SpinnerLine />);
  const isConstructed = useRef(false);
  if (!isConstructed.current) {
    isConstructed.current = true;
    (async () => {
      try {
        switch (type) {
          case 'Agent Setting': return setName((await client.graphql({ query: 'query GetAgentSettings($id: [Int]) { getAgentSettings(id: $id) { agent_settings { agent_name } } }', variables: { id } }))?.data.getAgentSettings.agent_settings?.[0].agent_name);
          case 'AI Platform': return setName(id);
          case 'Deal': return setName((await client.graphql({ query: 'query GetDeals($id: [Int]) { getDeals(id: $id) { deals { deal_name } } }', variables: { id } }))?.data.getDeals.deals?.[0].deal_name);
          case 'Protocol': return setName((await client.graphql({ query: 'query GetProtocols($id: [Int]) { getProtocols(id: $id) { protocols { protocol_name } } }', variables: { id } }))?.data.getProtocols.protocols?.[0].protocol_name);
          case 'Recruitment Source': return setName((await client.graphql({ query: 'query GetRecruitmentSources($id: [Int]) { getRecruitmentSources(id: $id) { recruitment_sources { source_name } } }', variables: { id } }))?.data.getRecruitmentSources.recruitment_sources?.[0].source_name);
          case 'Segment': return setName((await client.graphql({ query: 'query GetSegments($id: [Int]) { getSegments(id: $id) { segments { name } } }', variables: { id } }))?.data.getSegments.segments?.[0].name);
          case 'Site': return setName((await client.graphql({ query: 'query GetSites($id: [Int]) { getSites(id: $id) { sites { site_name } } }', variables: { id } }))?.data.getSites.sites?.[0].site_name);
          case 'Study': return setName((await client.graphql({ query: 'query GetStudies($id: [Int]) { getStudies(id: $id) { studies { study_name } } }', variables: { id } }))?.data.getStudies.studies?.[0].study_name);
          case 'Subject': return setName((await client.graphql({ query: 'query GetSubjects($id: [Int]) { getSubjects(id: $id) { subjects { full_name_last } } }', variables: { id } }))?.data.getSubjects.subjects?.[0].full_name_last);
          case 'Workflow': return setName((await client.graphql({ query: 'query GetWorkflows($id: [Int]) { getWorkflows(id: $id) { workflows { name } } }', variables: { id } }))?.data.getWorkflows.workflows?.[0].name);
        }
      } catch (error) {
        console.error(error);
      }
    })();
  }
  return name;
};

const new_id = ({ id, type }) => {
  return id === 'new' ? `New ${type}` : <Name id={id} type={type} />;
};

const delete_search = ({ key, search }) => {
  const _key = Array.isArray(key) ? key : [key];
  const queryParams = new URLSearchParams(search);
  _key.forEach(e => queryParams.delete(e));
  return queryParams.toString();
};

const DealsSubjectsListDetail = (props) => {
  const location = useLocation();
  const navigate = useNavigate();
  const id = useParams().id;
  const close = () => navigate({ pathname: '/app/customers/deals/subjects', search: location.search });
  const links = [];
  links.push({ label: 'Studies', url: { pathname: '/app/customers/deals', search: delete_search({ key: ['deal_id', 'deal_name'], search: location.search }) } });

  const url = new URLSearchParams(location.search);
  const deal_name = url.get('deal_name');
  if (deal_name) {
    links.push({
      label: `${deal_name} (Subjects)`,
      ...(id ? { url: { pathname: '/app/customers/deals/subjects', search: location.search } } : undefined)
    });
  }
  if (id) {
    links.push({ label: new_id({ id, type: 'Subject' }) });
  }

  return (
    <div onClick={event => event.stopPropagation()}>
      {id ? (
        <SubjectEntry {...props} links={links} onCancel={close} onSave={close} />
      ) : (
        <SubjectsList links={links} path="/app/customers/deals/subjects/" showDashboard={false} />
      )}
    </div>
  );
};

const DealsListDetail = (props) => {
  const location = useLocation();
  const navigate = useNavigate();
  const _id = useParams().id;
  const id = _id === 'new' ? undefined : Number(_id);
  const close = () => navigate('/app/customers/deals');

  return (
    <div onClick={event => event.stopPropagation()}>
      {_id ? (
        <DealEntry {...props} id={id} links={[{ label: 'Deals', url: { pathname: '/app/customers/deals', search: location.search } }, { label: new_id({ id: _id, type: 'Deal' }) }]} onCancel={close} onSave={close} />
      ) : (
        <DealsList />
      )}
    </div>
  );
};


const QuestionnairesSubjectsListDetail = (props) => {
  const location = useLocation();
  const navigate = useNavigate();
  const id = useParams().id;
  const close = () => navigate({ pathname: '/app/customers/questionnaires/subjects', search: location.search });
  
  
  const url = new URLSearchParams(location.search);
  const questionnaire_name = url.get('questionnaire_name');
  const questionnaire_id = url.get('questionnaire_id');
  const questionnaire_outcome = url.get('questionnaire_outcome');

  const links = [];
  links.push({ label: 'Questionnaires', url: { pathname: '/app/customers/questionnaires', search: delete_search({ key: ['questionnaire_id', 'questionnaire_name'], search: location.search }) } });

  if (questionnaire_name) {
    links.push({
      label: `${questionnaire_name} (${questionnaire_outcome ? questionnaire_outcome + ' / ' : ''}Subjects)`,
      ...(id ? { url: { pathname: '/app/customers/questionnaires/subjects', search: location.search } } : undefined)
    });
  }
  if (id) {
    links.push({ label: new_id({ id, type: 'Subject' }) });
  }

  return (
    <div onClick={event => event.stopPropagation()}>
      {id ? (
       
        <SubjectEntry {...props} links={links} onCancel={close} onSave={close} />
      ) : (
       
        <SubjectsList 
            links={links} 
            path="/app/customers/questionnaires/subjects/" 
            showDashboard={false}
            
            questionnaire_id={questionnaire_id ? parseInt(questionnaire_id) : undefined}
            questionnaire_name={questionnaire_name}
        />
      )}
    </div>
  );
};


const ProtocolsSubjectsListDetail = (props) => {
  const location = useLocation();
  const navigate = useNavigate();
  const id = useParams().id;
  const close = () => navigate({ pathname: '/app/customers/protocols/subjects', search: location.search });
  const links = [];
  links.push({ label: 'Protocols', url: { pathname: '/app/customers/protocols', search: delete_search({ key: 'protocol_name', search: location.search }) } });

  const url = new URLSearchParams(location.search);
  const protocol_name = url.get('protocol_name');
  if (protocol_name) {
    links.push({
      label: `${protocol_name} (Subjects)`,
      ...(id ? { url: { pathname: '/app/customers/protocols/subjects', search: location.search } } : undefined)
    });
  }
  if (id) {
    links.push({ label: new_id({ id, type: 'Subject' }) });
  }

  return (
    <div onClick={event => event.stopPropagation()}>
      {id ? (
        <SubjectEntry {...props} links={links} onCancel={close} onSave={close} />
      ) : (
        <SubjectsList links={links} path="/app/customers/protocols/subjects/" showDashboard={false} />
      )}
    </div>
  );
};

const ProtocolsListDetail = (props) => {
  const location = useLocation();
  const navigate = useNavigate();
  const _id = useParams().id;
  const id = _id === 'new' ? undefined : Number(_id);
  const close = () => navigate('/app/customers/protocols');

  return (
    <div onClick={event => event.stopPropagation()}>
      {_id ? (
        <ProtocolEntry {...props} id={id} links={[{ label: 'Protocols', url: { pathname: '/app/customers/protocols', search: location.search } }, { label: new_id({ id: _id, type: 'Protocol' }) }]} onCancel={close} onSave={close} />
      ) : (
        <ProtocolsList />
      )}
    </div>
  );
};

const SegmentsSubjectsListDetail = (props) => {
  const location = useLocation();
  const navigate = useNavigate();
  const id = useParams().id;
  const close = () => navigate({ pathname: '/app/customers/segments/subjects', search: location.search });
  const links = [];
  links.push({ label: 'Segments', url: { pathname: '/app/customers/segments', search: delete_search({ key: 'segment_name', search: location.search }) } });

  const url = new URLSearchParams(location.search);
  const segment_name = url.get('segment_name');
  if (segment_name) {
    links.push({
      label: `${segment_name} (Subjects)`,
      ...(id ? { url: { pathname: '/app/customers/segments/subjects', search: location.search } } : undefined)
    });
  }
  if (id) {
    links.push({ label: new_id({ id, type: 'Subject' }) });
  }

  return (
    <div onClick={event => event.stopPropagation()}>
      {id ? (
        <SubjectEntry {...props} links={links} onCancel={close} onSave={close} />
      ) : (
        <SubjectsList links={links} path="/app/customers/segments/subjects/" showDashboard={false} />
      )}
    </div>
  );
};

const RecruitmentSourcesSubjectsListDetail = (props) => {
  const location = useLocation();
  const navigate = useNavigate();
  const id = useParams().id;
  const close = () => navigate({ pathname: '/app/customers/recruitment_sources/subjects', search: location.search });
  const links = [];
  links.push({ label: 'Recruitment Sources', url: { pathname: '/app/customers/recruitment_sources', search: delete_search({ key: 'recruitment_name', search: location.search }) } });

  const url = new URLSearchParams(location.search);
  const recruitment_name = url.get('recruitment_name');
  if (recruitment_name) {
    links.push({
      label: `${recruitment_name} (Subjects)`,
      ...(id ? { url: { pathname: '/app/customers/recruitment_sources/subjects', search: location.search } } : undefined)
    });
  }
  if (id) {
    links.push({ label: new_id({ id, type: 'Subject' }) });
  }

  return (
    <div onClick={event => event.stopPropagation()}>
      {id ? (
        <SubjectEntry {...props} links={links} onCancel={close} onSave={close} />
      ) : (
        <SubjectsList links={links} path="/app/customers/recruitment_sources/subjects/" showDashboard={false} />
      )}
    </div>
  );
};

const StudiesSubjectsListDetail = (props) => {
  const location = useLocation();
  const navigate = useNavigate();
  const id = useParams().id;
  const close = () => navigate({ pathname: '/app/customers/studies/subjects', search: location.search });
  const links = [];
  links.push({ label: 'Studies', url: { pathname: '/app/customers/studies', search: delete_search({ key: ['protocol_id', 'protocol_name', 'study_name'], search: location.search }) } });

  const url = new URLSearchParams(location.search);
  const study_name = url.get('study_name');
  const protocol_name = url.get('protocol_name');
  if (study_name) {
    links.push({
      label: `${study_name} (Subjects)`,
      ...(id ? { url: { pathname: '/app/customers/studies/subjects', search: location.search } } : undefined)
    });
  } else if (protocol_name) {
    links.push({
      label: `${protocol_name} (Candidates)`,
      ...(id ? { url: { pathname: '/app/customers/studies/subjects', search: location.search } } : undefined)
    });
  }
  if (id) {
    links.push({ label: new_id({ id, type: 'Subject' }) });
  }

  return (
    <div onClick={event => event.stopPropagation()}>
      {id ? (
        <SubjectEntry {...props} links={links} onCancel={close} onSave={close} />
      ) : (
        <SubjectsList links={links} path="/app/customers/studies/subjects/" showDashboard={false} />
      )}
    </div>
  );
};

const SegmentsListDetail = (props) => {
  const location = useLocation();
  const navigate = useNavigate();
  const _id = useParams().id;
  const id = _id === 'new' ? undefined : Number(_id);
  const close = () => navigate('/app/customers/segments');

  return (
    <div onClick={event => event.stopPropagation()}>
      {_id ? (
        <SegmentEntryV1 {...props} id={id} links={[{ label: 'Segments', url: { pathname: '/app/customers/segments', search: location.search } }, { label: new_id({ id: _id, type: 'Segment' }) }]} onCancel={close} onSave={close} />
      ) : (
        <SegmentsList />
      )}
    </div>
  );
};

const SitesSubjectsListDetail = (props) => {
  const location = useLocation();
  const navigate = useNavigate();
  const id = useParams().id;
  const close = () => navigate({ pathname: '/app/customers/sites/subjects', search: location.search });
  const links = [];
  links.push({ label: 'Sites', url: { pathname: '/app/customers/sites', search: delete_search({ key: 'site_name', search: location.search }) } });

  const url = new URLSearchParams(location.search);
  const site_name = url.get('site_name');
  if (site_name) {
    links.push({
      label: `${site_name} (Subjects)`,
      ...(id ? { url: { pathname: '/app/customers/sites/subjects', search: location.search } } : undefined)
    });
  }
  if (id) {
    links.push({ label: new_id({ id, type: 'Subject' }) });
  }

  return (
    <div onClick={event => event.stopPropagation()}>
      {id ? (
        <SubjectEntry {...props} links={links} onCancel={close} onSave={close} />
      ) : (
        <SubjectsList links={links} path="/app/customers/sites/subjects/" showDashboard={false} />
      )}
    </div>
  );
};

const SitesListDetail = (props) => {
  const location = useLocation();
  const navigate = useNavigate();
  const id = useParams().id;
  const close = () => navigate('/app/customers/sites');

  return (
    <>
      {id ? (
        <SiteEntry {...props} links={[{ label: 'Sites', url: '/app/customers/sites' }, { label: new_id({ id, type: 'Site' }) }]} onCancel={close} onSave={close} />
      ) : (
        <SitesList />
      )}
    </>
  );
};

const StudiesListDetail = (props) => {
  const location = useLocation();
  const navigate = useNavigate();
  const _id = useParams().id;
  const id = _id === 'new' ? undefined : Number(_id);
  const close = () => navigate('/app/customers/studies');

  return (
    <div onClick={event => event.stopPropagation()}>
      {_id ? (
        <StudyEntry {...props} id={id} links={[{ label: 'Studies', url: { pathname: '/app/customers/studies', search: location.search } }, { label: new_id({ id: _id, type: 'Study' }) }]} onCancel={close} onSave={close} />
      ) : (
        <StudiesList />
      )}
    </div>
  );
};

const RecruitmentSourcesListDetail = (props) => {
  const location = useLocation();
  const navigate = useNavigate();
  const _id = useParams().id;
  const id = _id === 'new' ? undefined : Number(_id);
  const close = () => navigate('/app/customers/recruitment_sources');

  return (
    <div onClick={event => event.stopPropagation()}>
      {_id ? (
        <RecruitmentSourceEntry {...props} id={id} links={[{ label: 'Recruitment Sources', url: { pathname: '/app/customers/recruitment_sources', search: location.search } }, { label: new_id({ id: _id, type: 'Recruitment Source' }) }]} onCancel={close} onSave={close} />
      ) : (
        <RecruitmentSourcesList />
      )}
    </div>
  );
};

const WorkflowsSubjectsListDetail = (props) => {
  const location = useLocation();
  const navigate = useNavigate();
  const id = useParams().id;
  const close = () => navigate({ pathname: '/app/customers/workflows/subjects', search: location.search });
  const links = [];
  links.push({ label: 'Workflows', url: { pathname: '/app/customers/workflows', search: delete_search({ key: ['next_task_id', 'next_touchpoint', 'workflow_name'], search: location.search }) } });

  const url = new URLSearchParams(location.search);
  const workflow_name = url.get('workflow_name');
  const next_touchpoint = url.get('next_touchpoint');
  if (workflow_name) {
    links.push({
      label: `${workflow_name} (Subjects)`,
      ...(id || next_touchpoint ? { url: { pathname: '/app/customers/workflows/subjects', search: delete_search({ key: ['next_task_id', 'next_touchpoint'], search: location.search }) } } : undefined)
    });
  }
  if (next_touchpoint) {
    links.push({
      label: `${next_touchpoint}`,
      ...(id ? { url: { pathname: '/app/customers/workflows/subjects', search: location.search } } : undefined)
    });
  }
  if (id) {
    links.push({ label: new_id({ id, type: 'Subject' }) });
  }

  return (
    <div onClick={event => event.stopPropagation()}>
      {id ? (
        <SubjectEntry {...props} links={links} onCancel={close} onSave={close} />
      ) : (
        <SubjectsList links={links} path="/app/customers/workflows/subjects/" showDashboard={false} />
      )}
    </div>
  );
};

const WorkflowsListDetail = (props) => {
  const location = useLocation();
  const navigate = useNavigate();
  const _id = useParams().id;
  const id = _id === 'new' ? undefined : Number(_id);
  const close = () => navigate('/app/customers/workflows');

  return (
    <div onClick={event => event.stopPropagation()}>
      {_id ? (
        <WorkflowBuilder {...props} id={id} links={[{ label: 'Workflows', url: { pathname: '/app/customers/workflows', search: location.search } }, { label: new_id({ id: _id, type: 'Workflow' }) }]} onCancel={close} onSave={close} />
      ) : (
        <WorkflowsList />
      )}
    </div>
  );
};

const AgentSettingsListDetail = (props) => {
  const location = useLocation();
  const navigate = useNavigate();
  const id = useParams().id;
  const list_pathname = location.pathname.includes('/ai_platforms') ?
    '/app/customers/agent_settings/ai_platforms' : '/app/customers/agent_settings';
  const close = () => navigate(list_pathname);

  return (
    <>
      {id ? <>
        {location.pathname.includes('/ai_platforms') ? (
          <AIPlatformEntry {...props} links={[{ label: 'AI Platforms', url: '/app/customers/agent_settings/ai_platforms' }, { label: new_id({ id, type: 'AI Platform' }) }]} onCancel={close} onSave={close} />
        ) : (
          <AgentSettingEntry {...props} links={[{ label: 'Agent Settings', url: '/app/customers/agent_settings' }, { label: new_id({ id, type: 'Agent Setting' }) }]} onCancel={close} onSave={close} />
        )}
      </> : (
        <AgentSettingsList />
      )}
    </>
  );
};

const CustomerEngagement = () => {
  const [open, setOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const rootContext = useContext(RootContext);
  const auth = rootContext.get('auth');

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className="container-fluid">
      <Routes>
        {/* <Route path="campaigns/:id?" element={<CampaignsListDetail />} /> */}
        <Route
          path="deals/subjects/:id?"
          element={<DealsSubjectsListDetail />}
        />
        <Route path="deals/:id?" element={<DealsListDetail />} />
        
        <Route
          path="protocols/subjects/:id?"
          element={<ProtocolsSubjectsListDetail />}
        />
        <Route path="protocols/:id?" element={<ProtocolsListDetail />} />
        <Route
          path="segments/subjects/:id?"
          element={<SegmentsSubjectsListDetail />}
        />
        <Route path="segments/:id?" element={<SegmentsListDetail />} />
        <Route path="sites/:id?" element={<SitesListDetail />} />
        <Route
          path="sites/subjects/:id?"
          element={<SitesSubjectsListDetail />}
        />
        <Route path="studies/:id?" element={<StudiesListDetail />} />
        <Route
          path="studies/subjects/:id?"
          element={<StudiesSubjectsListDetail />}
        />
        <Route
          path="recruitment_sources/:id?"
          element={<RecruitmentSourcesListDetail />}
        />
        <Route
          path="recruitment_sources/subjects/:id?"
          element={<RecruitmentSourcesSubjectsListDetail />}
        />
        <Route
          path="workflows/subjects/:id?"
          element={<WorkflowsSubjectsListDetail />}
        />
        <Route path="workflows/:id?" element={<WorkflowsListDetail />} />

       
        <Route
          path="questionnaires/subjects/:id?"
          element={<QuestionnairesSubjectsListDetail />}
        />
        
        <Route path="questionnaires">
          <Route index element={<QuestionnaireList />} />
          <Route element={<QuestionnaireLayout />}>
            <Route path="setup" element={<QuestionnaireSetup />} />
            <Route path="details" element={<QuestionnaireDetails />} />
            <Route path="questions" element={<QuestionnaireQuestions />} />
            <Route path="review" element={<QuestionnaireReview />} />

            <Route path=":id/setup" element={<QuestionnaireSetup />} />
            <Route path=":id/details" element={<QuestionnaireDetails />} />
            <Route path=":id/questions" element={<QuestionnaireQuestions />} />
            <Route path=":id/review" element={<QuestionnaireReview />} />
          </Route>
        </Route>
        {/* ----------------------------------- */}

        {auth.groups?.includes("admin") && (
          <>
            <Route
              path="agent_settings/ai_platforms/:id?"
              element={<AgentSettingsListDetail />}
            />
            <Route
              path="agent_settings/:id?"
              element={<AgentSettingsListDetail />}
            />
          </>
        )}

        <Route path="privacy" element={<Privacy showTitle={true} />} />
        <Route
          path="*"
          element={
            <Navigate
              to={{ pathname: "studies", search: location.search }}
              replace
            />
          }
        />
      </Routes>
    </div>
  );
};

export default CustomerEngagement;